import React, { useMemo, useState } from 'react';
import { useLang } from '../../contexts/LangContext';
import { Apartment } from '../../api/apartmentsApi';
import { UNASSIGNED_APARTMENT_GROUP_ID } from '../../constants/apartmentGroups';
import { apartmentMatchesSearchTerm } from '../../utils/apartmentCodeSearch';

interface ParsedApartmentGroupFilter {
  groupIds: string[];
  includeUnassigned: boolean;
}

function parseApartmentGroupFilter(
  apartmentGroupIds?: string[],
): ParsedApartmentGroupFilter {
  const ids = apartmentGroupIds || [];
  return {
    groupIds: ids.filter((id) => id !== UNASSIGNED_APARTMENT_GROUP_ID),
    includeUnassigned: ids.includes(UNASSIGNED_APARTMENT_GROUP_ID),
  };
}

function apartmentMatchesGroupFilter(
  apartment: { id: string; groupId?: string | null },
  filter: ParsedApartmentGroupFilter,
): boolean {
  const hasFilter = filter.groupIds.length > 0 || filter.includeUnassigned;
  if (!hasFilter) {
    return true;
  }
  if (filter.includeUnassigned && !apartment.groupId) {
    return true;
  }
  return !!apartment.groupId && filter.groupIds.includes(apartment.groupId);
}

interface ApartmentMultiSelectProps {
  apartments: Apartment[];
  selectedApartmentIds: string[];
  selectedGroupIds?: string[];
  onChange: (apartmentIds: string[]) => void;
  disabled?: boolean;
}

const ApartmentMultiSelect: React.FC<ApartmentMultiSelectProps> = ({
  apartments,
  selectedApartmentIds,
  selectedGroupIds = [],
  onChange,
  disabled = false,
}) => {
  const { t } = useLang();
  const [searchTerm, setSearchTerm] = useState('');

  const scopedApartments = useMemo(() => {
    if (!selectedGroupIds.length) {
      return apartments;
    }
    const filter = parseApartmentGroupFilter(selectedGroupIds);
    return apartments.filter((apt) => apartmentMatchesGroupFilter(apt, filter));
  }, [apartments, selectedGroupIds]);

  const filteredApartments = useMemo(() => {
    if (!searchTerm.trim()) {
      return scopedApartments;
    }
    return scopedApartments.filter((apt) =>
      apartmentMatchesSearchTerm(
        {
          code: apt.code,
          name: apt.name,
          ownerName: apt.ownerName,
          usageTypeName: apt.usageType?.name,
          usageTypeCode: apt.usageType?.code,
        },
        searchTerm,
      ),
    );
  }, [scopedApartments, searchTerm]);

  const toggleApartment = (apartmentId: string) => {
    if (disabled) return;
    onChange(
      selectedApartmentIds.includes(apartmentId)
        ? selectedApartmentIds.filter((id) => id !== apartmentId)
        : [...selectedApartmentIds, apartmentId],
    );
  };

  const selectFiltered = () => {
    if (disabled) return;
    const merged = new Set([...selectedApartmentIds, ...filteredApartments.map((a) => a.id)]);
    onChange(Array.from(merged));
  };

  const clearSelection = () => {
    if (disabled) return;
    onChange([]);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Căn hộ (tùy chọn)
      </label>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Tìm mã căn (vd. a0501), loại sử dụng, chủ hộ..."
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2 disabled:bg-gray-100"
      />
      <div className="flex flex-wrap gap-2 mb-2">
        <button
          type="button"
          onClick={selectFiltered}
          disabled={disabled || filteredApartments.length === 0}
          className="text-xs text-cyan-600 hover:underline disabled:opacity-50"
        >
          Chọn kết quả lọc ({filteredApartments.length})
        </button>
        <button
          type="button"
          onClick={clearSelection}
          disabled={disabled || selectedApartmentIds.length === 0}
          className="text-xs text-gray-500 hover:underline disabled:opacity-50"
        >
          Bỏ chọn căn hộ
        </button>
      </div>
      <div
        className={`border border-gray-200 rounded-lg max-h-48 overflow-y-auto p-2 space-y-1 ${
          disabled ? 'bg-gray-100 opacity-70' : ''
        }`}
      >
        {filteredApartments.length === 0 ? (
          <p className="text-sm text-gray-500 px-2 py-1">
            {scopedApartments.length === 0
              ? 'Không có căn hộ trong nhóm đã chọn'
              : 'Không tìm thấy căn hộ'}
          </p>
        ) : (
          filteredApartments.map((apt) => (
            <label
              key={apt.id}
              className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedApartmentIds.includes(apt.id)}
                onChange={() => toggleApartment(apt.id)}
                disabled={disabled}
                className="rounded border-gray-300 text-cyan-600"
              />
              <span className="text-sm font-medium text-gray-900">{apt.code}</span>
              <span className="text-xs text-gray-500 truncate">
                {apt.ownerName}
                {apt.group?.name ? ` · ${apt.group.name}` : ''}
              </span>
            </label>
          ))
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {selectedApartmentIds.length > 0
          ? t('selectedApartmentsCount').replace(
              '{count}',
              String(selectedApartmentIds.length),
            )
          : 'Bỏ trống để áp dụng theo nhóm đã chọn hoặc tất cả căn hộ.'}
      </p>
    </div>
  );
};

export default ApartmentMultiSelect;
